import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

export type ViewMode = "list" | "calendar" | "map" | "nearby";

const VIEW_MODES: ViewMode[] = ["list", "calendar", "map", "nearby"];

interface ViewModeContextValue {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

const ViewModeContext = createContext<ViewModeContextValue>({
  viewMode: "list",
  setViewMode: () => {},
});

function viewFromUrl(): ViewMode {
  const view = new URLSearchParams(window.location.search).get("view");
  return VIEW_MODES.includes(view as ViewMode) ? (view as ViewMode) : "list";
}

/**
 * The chosen view lives in the URL as well as in state.
 *
 * A distance search is worth sending to someone — "here is what is on near
 * you" — and a link that lands them on the default list, with the zip filter
 * applied but invisible, is a worse answer than no link. Written with
 * replaceState so switching tabs does not fill up the back button.
 */
export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewMode] = useState<ViewMode>(viewFromUrl);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (viewMode === "list") params.delete("view");
    else params.set("view", viewMode);
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      query ? `${window.location.pathname}?${query}` : window.location.pathname
    );
  }, [viewMode]);

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode }}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode() {
  return useContext(ViewModeContext);
}
